/**
 * AR-7 Fall Detection (Streaming)
 * Method ID: b1-fall-detection
 *
 * Physically-correct streaming fall detector driven by a four-state
 * state machine: Normal → FreeFall → Impact → Stationary.
 *
 *   Normal    : steady skiing; watch for freefall onset (resultant g drops
 *               below the freefall threshold for at least freefallMinMs).
 *   FreeFall  : weightless phase; watch for the impact spike (resultant g
 *               exceeds the impact threshold). Times out back to Normal.
 *   Impact    : deceleration after landing; track peak impact g and watch
 *               for the body coming to rest (resultant g near 1 g for at
 *               least stationaryMinMs). Times out back to Normal.
 *   Stationary: a fall is confirmed; emit a FallEvent and reset to Normal.
 *
 * 物理正确的流式摔倒检测，采用四态状态机：
 * Normal → FreeFall → Impact → Stationary。
 *
 * Deterministic per PO-5.4 (no random numbers).
 * 按 PO-5.4 确定性实现（无随机数）。
 */

import type { SkiTickData, FallEvent } from '../types/ski-data';
import { FallDetectionState, FallSensitivity } from '../types/enums';
import { AR7_PARAMS, GRAVITY } from '../constants/algorithm-params';

// ===== Context =====

/**
 * Mutable streaming state carried across frames.
 *
 * 跨帧携带的可变流式状态。
 */
export interface FallDetectionContext {
  /** Current state-machine state / 当前状态机状态 */
  state: FallDetectionState;
  /** Timestamp (ms) when the freefall phase began / 自由落体起始时间戳 */
  freefallStartTime: number | null;
  /** Timestamp (ms) of the impact spike / 冲击峰值时间戳 */
  impactTime: number | null;
  /** Timestamp (ms) when the stationary phase began / 静止起始时间戳 */
  stationaryStartTime: number | null;
  /** Timestamp (ms) of the most recent emitted fall / 最近一次摔倒时间戳 */
  lastFallTime: number;
  /** Timestamp (ms) when the current state was entered / 进入当前状态的时间戳 */
  stateEnterTime: number;
  /** Ordered trace of states visited during the current fall candidate / 当前摔倒候选的状态轨迹 */
  stateTrace: FallDetectionState[];
  /** Peak resultant g observed during the impact phase / 冲击阶段的峰值合成 g */
  peakImpactG: number;
}

/**
 * Create a fresh fall-detection context initialized to the Normal state.
 *
 * 创建一个初始处于 Normal 状态的摔倒检测上下文。
 *
 * @returns a new FallDetectionContext / 新的检测上下文
 */
export function createFallDetectionContext(): FallDetectionContext {
  return {
    state: FallDetectionState.Normal,
    freefallStartTime: null,
    impactTime: null,
    stationaryStartTime: null,
    lastFallTime: -Infinity,
    stateEnterTime: 0,
    stateTrace: [],
    peakImpactG: 0,
  };
}

// ===== Helpers =====

/**
 * Compute the resultant acceleration magnitude in g units.
 *
 *   |a| = sqrt(x² + y² + z²) / GRAVITY
 *
 * 计算合成加速度（g 单位）的模长。
 *
 * @param acceleration - raw acceleration vector in m/s² / 原始加速度向量（m/s²）
 * @returns resultant magnitude in g / 合成加速度（g）
 */
export function computeResultantG(acceleration: {
  x: number;
  y: number;
  z: number;
}): number {
  const mag = Math.sqrt(
    acceleration.x ** 2 + acceleration.y ** 2 + acceleration.z ** 2
  );
  return mag / GRAVITY;
}

// ===== Per-Frame State Machine =====

/**
 * Result of processing a single frame.
 *
 * 处理单帧的结果。
 */
export interface ProcessFrameResult {
  /** Updated context (immutable copy) / 更新后的上下文（不可变拷贝） */
  ctx: FallDetectionContext;
  /** Whether a fall was confirmed on this frame / 本帧是否确认摔倒 */
  fallDetected: boolean;
  /** Emitted fall event, or null / 产出的摔倒事件，或 null */
  fallEvent: FallEvent | null;
}

/**
 * Process a single frame, advancing the state machine.
 *
 * The context is treated as immutable: a shallow copy is returned with the
 * updated fields. This keeps the streaming API pure and deterministic.
 *
 * 处理单帧并推进状态机。上下文按不可变处理，返回浅拷贝，保证流式接口
 * 纯粹且确定。
 *
 * @param ctx - current context / 当前上下文
 * @param frame - one SkiTickData sample / 一帧滑雪数据
 * @param sensitivity - detection sensitivity (default Medium) / 检测灵敏度（默认 Medium）
 * @returns updated context plus optional fall event / 更新后的上下文及可选摔倒事件
 */
export function processFrame(
  ctx: FallDetectionContext,
  frame: SkiTickData,
  sensitivity: FallSensitivity = FallSensitivity.Medium
): ProcessFrameResult {
  const params = AR7_PARAMS[sensitivity];
  const resultantG = computeResultantG(frame.acceleration);
  const t = frame.timestamp_ms;

  let fallDetected = false;
  let fallEvent: FallEvent | null = null;

  // Shallow-copy the context so callers retain the previous state if needed.
  // 浅拷贝上下文，调用方可保留前一状态。
  const updatedCtx: FallDetectionContext = { ...ctx };

  switch (ctx.state) {
    // ---------------------------------------------------------------
    // Normal: watch for freefall onset.
    // Normal：监测自由落体起始。
    // ---------------------------------------------------------------
    case FallDetectionState.Normal: {
      if (resultantG < params.freefallG) {
        if (updatedCtx.freefallStartTime === null) {
          updatedCtx.freefallStartTime = t;
        }
        // Transition to FreeFall once the low-g condition persists long enough.
        // 低 g 条件持续足够长时间后转入 FreeFall。
        if (
          updatedCtx.freefallStartTime !== null &&
          t - updatedCtx.freefallStartTime >= params.freefallMinMs
        ) {
          updatedCtx.state = FallDetectionState.FreeFall;
          updatedCtx.stateEnterTime = t;
          updatedCtx.stateTrace = [
            ...ctx.stateTrace,
            FallDetectionState.FreeFall,
          ];
        }
      } else {
        // Brief turbulence is not freefall; reset the timer.
        // 短暂扰动不算自由落体，重置计时。
        updatedCtx.freefallStartTime = null;
      }
      break;
    }

    // ---------------------------------------------------------------
    // FreeFall: watch for the impact spike.
    // FreeFall：监测冲击峰值。
    // ---------------------------------------------------------------
    case FallDetectionState.FreeFall: {
      if (resultantG > params.impactG) {
        updatedCtx.state = FallDetectionState.Impact;
        updatedCtx.impactTime = t;
        updatedCtx.stateEnterTime = t;
        updatedCtx.peakImpactG = resultantG;
        updatedCtx.stateTrace = [
          ...ctx.stateTrace,
          FallDetectionState.Impact,
        ];
      } else if (t - ctx.stateEnterTime > params.phaseTimeoutMs) {
        // No impact within the timeout — likely a jump, not a fall.
        // 超时未检测到冲击，多为跳跃而非摔倒，回到 Normal。
        updatedCtx.state = FallDetectionState.Normal;
        updatedCtx.freefallStartTime = null;
        updatedCtx.stateEnterTime = t;
      }
      break;
    }

    // ---------------------------------------------------------------
    // Impact: watch for the body coming to rest near 1 g.
    // Impact：监测身体静止（合成 g 趋近 1）。
    // ---------------------------------------------------------------
    case FallDetectionState.Impact: {
      if (Math.abs(resultantG - 1.0) < params.stationaryHalfBandG) {
        if (updatedCtx.stationaryStartTime === null) {
          updatedCtx.stationaryStartTime = t;
        }
        if (
          updatedCtx.stationaryStartTime !== null &&
          t - updatedCtx.stationaryStartTime >= params.stationaryMinMs
        ) {
          updatedCtx.state = FallDetectionState.Stationary;
          updatedCtx.stateEnterTime = t;
          updatedCtx.stateTrace = [
            ...ctx.stateTrace,
            FallDetectionState.Stationary,
          ];
        }
      } else {
        // Still decelerating; reset the stationary timer and keep tracking
        // the peak impact g in case a larger spike follows.
        // 仍在减速，重置静止计时并继续追踪峰值冲击 g。
        updatedCtx.stationaryStartTime = null;
        if (resultantG > updatedCtx.peakImpactG) {
          updatedCtx.peakImpactG = resultantG;
        }
      }
      // If neither rest nor a continued spike is observed within the
      // timeout, abandon the fall candidate.
      // 超时内既未静止也未持续冲击，放弃本次摔倒候选。
      if (t - ctx.stateEnterTime > params.phaseTimeoutMs) {
        updatedCtx.state = FallDetectionState.Normal;
        updatedCtx.stateEnterTime = t;
        updatedCtx.stationaryStartTime = null;
      }
      break;
    }

    // ---------------------------------------------------------------
    // Stationary: a fall is confirmed — emit the event and reset.
    // Stationary：摔倒确认——产出事件并重置。
    // ---------------------------------------------------------------
    case FallDetectionState.Stationary: {
      const freefallDuration =
        ctx.freefallStartTime !== null && ctx.impactTime !== null
          ? ctx.impactTime - ctx.freefallStartTime
          : 0;
      // Severity grows with excess impact g: 1 g excess → 50, 2 g → 100.
      // 严重程度随超额冲击 g 增长：超额 1 g → 50，超额 2 g → 100。
      const severity = Math.min(100, (updatedCtx.peakImpactG - 1) * 50);

      fallEvent = {
        timestamp_ms: ctx.freefallStartTime ?? t,
        severity: Math.max(0, severity),
        freefall_duration: freefallDuration,
        impact_g: updatedCtx.peakImpactG,
        state_trace: [...updatedCtx.stateTrace],
      };
      fallDetected = true;

      // Reset to Normal for the next fall candidate.
      // 重置为 Normal 以检测下一次摔倒。
      updatedCtx.state = FallDetectionState.Normal;
      updatedCtx.freefallStartTime = null;
      updatedCtx.impactTime = null;
      updatedCtx.stationaryStartTime = null;
      updatedCtx.lastFallTime = t;
      updatedCtx.stateEnterTime = t;
      updatedCtx.peakImpactG = 0;
      updatedCtx.stateTrace = [];
      break;
    }
  }

  return { ctx: updatedCtx, fallDetected, fallEvent };
}

// ===== Batch Entry Point =====

/**
 * Process an entire frame sequence and return all detected fall events.
 *
 * This is a convenience wrapper around {@link processFrame} for offline
 * analysis of a complete run.
 *
 * 处理完整帧序列并返回所有检测到的摔倒事件。
 * 是 {@link processFrame} 的批量封装，用于完整滑行的离线分析。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param sensitivity - detection sensitivity (default Medium) / 检测灵敏度（默认 Medium）
 * @returns array of FallEvent / 摔倒事件数组
 */
export function detectFalls(
  frames: SkiTickData[],
  sensitivity: FallSensitivity = FallSensitivity.Medium
): FallEvent[] {
  let ctx = createFallDetectionContext();
  const events: FallEvent[] = [];
  for (const frame of frames) {
    const result = processFrame(ctx, frame, sensitivity);
    ctx = result.ctx;
    if (result.fallEvent) {
      events.push(result.fallEvent);
    }
  }
  return events;
}
